import { ExclamationCircleOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { Button, Modal, Tooltip } from 'antd';
import { Dayjs } from 'dayjs';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import TaskDatePicker from './TaskDatePicker';

interface PrintTaskDateModalProps {
    visible: boolean;
    onCancel: () => void;
    /**
     * 点「确认」：回传所选任务日期，由调用方复核并打开打印页。
     * 调用方须在 await 之前同步 window.open，避免被浏览器拦截弹窗。
     * 返回 false 表示所选日期已不可选，对话框清空日期并提示重新选择。
     */
    onConfirm: (taskDate: Dayjs) => Promise<boolean>;
    /** 仓库当前时间，决定日期面板中哪些日期可选 */
    warehouseNow: Dayjs;
}

/**
 * 打印提货单前选择任务日期（R4）：默认留空，未选不可确认，规则与发布确认弹窗一致。
 */
const PrintTaskDateModal: React.FC<PrintTaskDateModalProps> = ({
    visible,
    onCancel,
    onConfirm,
    warehouseNow,
}) => {
    const { t } = useTranslation();
    const [taskDate, setTaskDate] = useState<Dayjs | null>(null);
    const [dateError, setDateError] = useState<string | null>(null);
    const [confirming, setConfirming] = useState(false);

    // 每次打开都重新留空
    useEffect(() => {
        if (visible) {
            setTaskDate(null);
            setDateError(null);
            setConfirming(false);
        }
    }, [visible]);

    const handleConfirm = async () => {
        if (!taskDate) return;
        setConfirming(true);
        try {
            const accepted = await onConfirm(taskDate);
            if (!accepted) {
                setTaskDate(null);
                setDateError(t('delivery.routes.message.printTaskDateChanged'));
            }
        } finally {
            setConfirming(false);
        }
    };

    return (
        <Modal
            title={null}
            open={visible}
            onCancel={onCancel}
            width={500}
            footer={null}
            closable={false}
            destroyOnClose
        >
            <div className="p-4">
                <div className="flex items-center gap-3 mb-8">
                    <ExclamationCircleOutlined className="text-3xl text-red-500" />
                    <span className="text-2xl font-bold text-black">
                        {t('delivery.routes.message.printTaskDateTitle')}
                    </span>
                </div>

                {/* 标签过长时日期框自动换行，避免撑出弹窗 */}
                <div className="flex flex-wrap items-start gap-x-2 gap-y-2 mb-8 pl-6">
                    <span className="text-lg font-medium mt-0.5">
                        {t('delivery.routes.label.taskDate')}
                        <Tooltip title={<div className="whitespace-pre-line">{t('delivery.routes.message.taskDateRuleTooltip')}</div>}>
                            <QuestionCircleOutlined className="cursor-help ml-1 text-base" style={{ color: '#7c3aed' }} />
                        </Tooltip>
                        :
                    </span>
                    <TaskDatePicker
                        value={taskDate}
                        onChange={(date) => { setTaskDate(date); setDateError(null); }}
                        warehouseNow={warehouseNow}
                        placeholder={t('delivery.routes.message.printTaskDatePlaceholder')}
                        errorText={dateError}
                    />
                </div>

                <div className="flex justify-end gap-3">
                    <Button size="large" onClick={onCancel} className="min-w-[120px]">
                        {t('delivery.routes.message.returnToModify')}
                    </Button>
                    <Button
                        type="primary"
                        size="large"
                        onClick={handleConfirm}
                        disabled={!taskDate}
                        loading={confirming}
                        className="min-w-[120px]"
                        style={taskDate ? { background: '#7c3aed' } : undefined}
                    >
                        {t('delivery.routes.message.confirmPublish')}
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

export default PrintTaskDateModal;
